<template>
  <div class="product-category-container">
    <h1 class="page-title">商品分类</h1>
    <el-card class="content-card">
      <div class="card-header">
        <el-button type="primary" @click="handleAdd">新增分类</el-button>
      </div>
      <div class="card-content">
        <el-table v-loading="tableLoading" :data="categories" border stripe style="width: 100%;">
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="name" label="分类名称" />
          <el-table-column prop="description" label="分类描述" width="300" show-overflow-tooltip />
          <el-table-column prop="sort_order" label="排序" width="100" />
          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.status === 'active' ? 'success' : 'danger'">
                {{ scope.row.status === 'active' ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" @click="handleEdit(scope.row)">
                编辑
              </el-button>
              <el-button 
                type="danger" 
                size="small" 
                @click="handleDelete(scope.row)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
    
    <!-- 新增/编辑分类抽屉 -->
    <el-drawer
      v-model="dialogVisible"
      :title="isEdit ? '编辑分类' : '新增分类'"
      direction="rtl"
      size="80%"
    >
      <el-form :model="form" ref="formRef" label-width="120px">
        <el-form-item label="分类名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入分类名称" />
        </el-form-item>
        <el-form-item label="分类描述" prop="description">
          <el-input 
            v-model="form.description" 
            type="textarea" 
            :rows="3" 
            placeholder="请输入分类描述"
          />
        </el-form-item>
        <el-form-item label="排序" prop="sort_order">
          <el-input-number 
            v-model="form.sort_order" 
            :min="0" 
            :step="1" 
            placeholder="请输入排序号"
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="form.status" placeholder="请选择状态">
            <el-option label="启用" value="active" />
            <el-option label="禁用" value="inactive" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确定</el-button>
        </span>
      </template>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '../../api/axios';

// 分类列表数据
const categories = ref([]);
// 对话框显示状态
const dialogVisible = ref(false);
// 表单引用
const formRef = ref();
// 是否为编辑模式
const isEdit = ref(false);
// 表格加载状态
const tableLoading = ref(false);

// 表单数据
const form = reactive({
  id: '',
  name: '',
  description: '',
  parent_id: '',
  sort_order: 0,
  status: 'active'
});



// 获取分类列表
const fetchCategories = async () => {
  try {
    tableLoading.value = true;
    const response = await request({
      url: '/manage/product-categories',
      method: 'get'
    });
    categories.value = response.data;
  } catch (error) {
    ElMessage.error('获取分类列表失败');
    console.error(error);
  } finally {
    tableLoading.value = false;
  }
};

// 页面加载时获取分类列表
onMounted(() => {
  fetchCategories();
});

// 新增分类
// eslint-disable-next-line no-unused-vars
const handleAdd = () => {
  isEdit.value = false;
  // 重置表单
  Object.assign(form, {
    id: '',
    name: '',
    description: '',
    parent_id: '',
    sort_order: 0,
    status: 'active'
  });
  dialogVisible.value = true;
};

// 编辑分类
// eslint-disable-next-line no-unused-vars
const handleEdit = (row) => {
  isEdit.value = true;
  // 填充表单数据
  Object.assign(form, {
    id: row.id,
    name: row.name,
    description: row.description,
    parent_id: row.parent_id || '',
    sort_order: row.sort_order,
    status: row.status
  });
  dialogVisible.value = true;
};

// 删除分类
// eslint-disable-next-line no-unused-vars
const handleDelete = (row) => {
  ElMessageBox.confirm('确定要删除该分类吗？', '警告', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      await request({
        url: `/manage/product-categories/${row.id}`,
        method: 'delete'
      });
      ElMessage.success('删除分类成功');
      fetchCategories();
    } catch (error) {
      ElMessage.error('删除分类失败');
      console.error(error);
    }
  }).catch(() => {
    // 取消删除操作
  });
};

// 提交表单
// eslint-disable-next-line no-unused-vars
const handleSubmit = async () => {
  if (!formRef.value) return;
  
  try {
    await formRef.value.validate();
    
    // 确保所有分类都是一级分类，设置parent_id为空
    form.parent_id = '';
    
    if (isEdit.value) {
      // 更新分类
      await request({
        url: `/manage/product-categories/${form.id}`,
        method: 'put',
        data: form
      });
      ElMessage.success('更新分类成功');
    } else {
      // 新增分类
      await request({
        url: '/manage/product-categories',
        method: 'post',
        data: form
      });
      ElMessage.success('新增分类成功');
    }
    
    dialogVisible.value = false;
    fetchCategories();
  } catch (error) {
    if (error.name === 'Error') {
      // 表单验证失败，不显示错误信息
      return;
    }
    ElMessage.error(isEdit.value ? '更新分类失败' : '新增分类失败');
    console.error(error);
  }
};
</script>

<style scoped>
.product-category-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.card-content {
  padding: 20px 0;
}
</style>